import * as _ from 'lodash';

import { CurrentSchoolYear } from '../../../../ng2/shared/constants/current-school-year.constant';
import { NextGraduationDate } from '../../../../ng2/shared/constants/graduation-date.constant';

export type TValidPriorityGroupKeys =
  | 'PLANNED_JAN_MARCH'
  | 'PLANNED_JUNE'
  | 'PLANNED_AUG'
  | 'PLANNED_BEYOND_AUG'
  | 'PLANNED_NON_GRAD'
  | 'PLAN_INCOMPLETE'
  | 'JUNIORS'
  | 'SOPHOMORES'
  | 'FRESHMAN'
  | 'NONE';

export type TValidStudentYearFilters = 1 | 2 | 3 | 4;

export interface IPriorityGroupDefinition {
  key: TValidPriorityGroupKeys;
  priorityVal: number;
  priorityLabel: string;
  studentPriorityGroupingValue: string;
  studentYearFilter: TValidStudentYearFilters;
  studentIds?: string[];
}

export class RegentsPrepPriorityGroupingsService {
  static $inject: string[] = ['Rollbar'];

  constructor(private Rollbar) {}

  private _priorityGroupings: IPriorityGroupDefinition[];

  private currentSchoolYearEnd = Number(CurrentSchoolYear.ENDFULL);
  private juniorCohort = this.currentSchoolYearEnd + 1;
  private sophomoreCohort = this.currentSchoolYearEnd + 2;
  private freshmanCohort = this.currentSchoolYearEnd + 3;

  private priorityGroupDefinitions = [
    {
      key: 'PLANNED_JAN_MARCH',
      priorityVal: 1,
      priorityLabel: `Planned January or March ${this.currentSchoolYearEnd} Grads needing Regents prep support.`,
      studentPriorityGroupingValue:
        'Planned Jan/March ' + this.currentSchoolYearEnd + ' Grad needing Regents prep support',
      studentYearFilter: 4,
    } as IPriorityGroupDefinition,
    {
      key: 'PLANNED_JUNE',
      priorityVal: 2,
      priorityLabel: `Planned June ${this.currentSchoolYearEnd} Grads needing Regents prep support.`,
      studentPriorityGroupingValue:
        'Planned June/March ' + this.currentSchoolYearEnd + ' Grad needing Regents prep support',
      studentYearFilter: 4,
    } as IPriorityGroupDefinition,
    {
      key: 'PLANNED_AUG',
      priorityVal: 3,
      priorityLabel: `Planned Aug ${this.currentSchoolYearEnd} Grads needing Regents prep support.`,
      studentPriorityGroupingValue: 'Planned Aug ' + this.currentSchoolYearEnd + ' Grad needing Regents prep support',
      studentYearFilter: 4,
    } as IPriorityGroupDefinition,
    {
      key: 'PLANNED_BEYOND_AUG',
      priorityVal: 4,
      priorityLabel: `Students planned to graduate beyond Aug ${
        this.currentSchoolYearEnd
      } needing Regents prep support.`,
      studentPriorityGroupingValue:
        'Planned Beyond Aug ' + this.currentSchoolYearEnd + ' Grad needing Regents prep support',
      studentYearFilter: 4,
    } as IPriorityGroupDefinition,
    {
      key: 'PLAN_INCOMPLETE',
      priorityVal: 5,
      priorityLabel: `Seniors with incomplete grad plans needing Regents prep support.`,
      studentPriorityGroupingValue: 'No Grad Plan needing Regents prep support',
      studentYearFilter: 4,
    } as IPriorityGroupDefinition,
    {
      key: 'JUNIORS',
      priorityVal: 6,
      priorityLabel: 'Active class of ' + this.juniorCohort + ' needing Regents prep support.',
      studentPriorityGroupingValue: this.juniorCohort + ' needing Regents prep support',
      studentYearFilter: 3,
      studentIds: [],
    } as IPriorityGroupDefinition,
    {
      key: 'SOPHOMORES',
      priorityVal: 7,
      priorityLabel: `Active class ${this.sophomoreCohort} students needing Regents prep support.`,
      studentPriorityGroupingValue: this.sophomoreCohort + ' needing Regents prep support',
      studentYearFilter: 2,
    } as IPriorityGroupDefinition,
    {
      key: 'FRESHMAN',
      priorityVal: 8,
      priorityLabel: `Active class of ${this.freshmanCohort} students needing Regents prep support.`,
      studentPriorityGroupingValue: `${this.freshmanCohort} needing Regents prep support`,
      studentYearFilter: 1,
    } as IPriorityGroupDefinition,
    {
      key: 'PLANNED_NON_GRAD',
      priorityVal: 9,
      priorityLabel: `Planned Non-Graduates needing Regents prep support.`,
      studentPriorityGroupingValue: 'Planned Non-Grad needing Regents prep support',
      studentYearFilter: 4,
    } as IPriorityGroupDefinition,
    {
      key: 'NONE',
      priorityVal: null,
      priorityLabel: `Students not needing Regents prep support.`,
      studentPriorityGroupingValue: `All planned exams supported`,
      studentYearFilter: null,
    } as IPriorityGroupDefinition,
  ];

  private setRegentsPrepPriorityGroupingDetails() {
    this._priorityGroupings = [];
    // figure out priority numbers
    const nextGradDateMonthOrder = NextGraduationDate.monthOrder;
    const janIsNext = nextGradDateMonthOrder === 1;
    const marchIsNext = nextGradDateMonthOrder === 2;
    const juneIsNext = nextGradDateMonthOrder === 3;
    const augIsNext = nextGradDateMonthOrder === 4;

    let groupings;
    if (janIsNext || marchIsNext) groupings = new Array(10);
    if (juneIsNext) groupings = new Array(9);
    if (augIsNext) groupings = new Array(8);

    let startingIndex;
    if (janIsNext || marchIsNext) startingIndex = 0;
    if (juneIsNext) startingIndex = 1;
    if (augIsNext) startingIndex = 2;

    _.forEach(groupings, (group, i) => {
      const groupIndex = startingIndex + i;
      this._priorityGroupings.push(this.priorityGroupDefinitions[groupIndex]);
      // set priority val prop
      if (this.priorityGroupDefinitions[groupIndex].priorityVal) {
        const priorityVal = i + 1;
        this._priorityGroupings[i].priorityVal = priorityVal;
        // overwrite studentPriorityGroupingValue to include priority val
        this._priorityGroupings[i].studentPriorityGroupingValue =
          priorityVal + '. ' + this._priorityGroupings[i].studentPriorityGroupingValue;
      }
    });
  }

  get priorityGroupings(): IPriorityGroupDefinition[] {
    if (!this._priorityGroupings) this.setRegentsPrepPriorityGroupingDetails();
    return this._priorityGroupings;
  }

  private _getPriorityGroupByKey(key: TValidPriorityGroupKeys): IPriorityGroupDefinition {
    const priorityGroupings = this.priorityGroupings;
    return _.find(priorityGroupings, { key });
  }

  public getStudentValueForPriorityGroupByKey(key: TValidPriorityGroupKeys): string {
    const prioirtyGroup = this._getPriorityGroupByKey(key);
    return prioirtyGroup.studentPriorityGroupingValue;
  }

  public getPriorityGroupsByStudentYearFilter(
    studentYearFilter?: TValidStudentYearFilters,
  ): IPriorityGroupDefinition[] {
    const priorityGroupings = this.priorityGroupings;
    if (studentYearFilter) {
      return _.filter(priorityGroupings, { studentYearFilter });
    } else {
      return priorityGroupings;
    }
  }
}
